import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, downloadCsv } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Mail, Phone, Loader2, Inbox, Download, ArrowRightLeft } from "lucide-react";

const STATUS_STYLES = {
  new: "bg-[#e31e24] text-white",
  contacted: "bg-amber-500/90 text-black",
  closed: "bg-white/15 text-white/70",
};

export const AdminEnquiries = ({ onConvert }) => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () =>
    api
      .listEnquiries()
      .then((res) => setItems(res.data))
      .catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.updateEnquiry(id, status);
      setItems((cur) => cur.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteEnquiry(id);
      setItems((cur) => cur.filter((i) => i.id !== id));
      toast.success("Enquiry deleted.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const exportCsv = () => {
    if (!items || items.length === 0) {
      toast.error("No enquiries to export.");
      return;
    }
    downloadCsv(
      `rayce-parts-enquiries-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "created_at", label: "Date" },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "part", label: "Part of Interest" },
        { key: "status", label: "Status" },
        { key: "message", label: "Message" },
      ],
      items
    );
    toast.success(`Exported ${items.length} enquiries.`);
  };

  if (items === null)
    return (
      <div className="grid place-items-center py-24 text-white/40">
        <Loader2 className="animate-spin" />
      </div>
    );

  const shown =
    filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = items.reduce(
    (a, i) => ({ ...a, [i.status]: (a[i.status] || 0) + 1 }),
    {}
  );

  return (
    <div data-testid="admin-enquiries">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {["all", "new", "contacted", "closed"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`enq-filter-${f}`}
            className={`font-mono-rp text-[11px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors ${
              filter === f
                ? "border-[#e31e24] text-white bg-[#e31e24]/15"
                : "border-white/15 text-white/50 hover:text-white"
            }`}
          >
            {f} {f !== "all" && `(${counts[f] || 0})`}
          </button>
        ))}
        <button
          onClick={exportCsv}
          data-testid="enq-export-csv"
          className="ml-auto flex items-center gap-2 font-mono-rp text-[11px] tracking-[0.15em] uppercase px-3 py-2 border border-white/15 text-white/60 hover:text-white hover:border-[#e31e24] transition-colors"
        >
          <Download size={13} /> Export CSV
        </button>
      </div>

      {shown.length === 0 ? (
        <div className="grid place-items-center py-24 text-white/40 border border-white/10 border-dashed">
          <Inbox size={28} />
          <p className="mt-3 text-sm">No enquiries here yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {shown.map((it) => (
            <div
              key={it.id}
              className="bg-[#141414] border border-white/10 p-5 md:p-6"
              data-testid={`enquiry-row-${it.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-2xl text-white">
                      {it.name}
                    </h3>
                    <span
                      className={`text-[10px] font-mono-rp uppercase tracking-widest px-2 py-1 ${
                        STATUS_STYLES[it.status]
                      }`}
                      data-testid={`enquiry-status-${it.id}`}
                    >
                      {it.status}
                    </span>
                    {it.part && (
                      <span className="text-[11px] font-mono-rp text-[#e31e24] border border-[#e31e24]/40 px-2 py-1">
                        {it.part}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-white/60">
                    <a
                      href={`mailto:${it.email}`}
                      className="flex items-center gap-1.5 hover:text-white"
                    >
                      <Mail size={13} /> {it.email}
                    </a>
                    {it.phone && (
                      <a
                        href={`tel:${it.phone}`}
                        className="flex items-center gap-1.5 hover:text-white"
                      >
                        <Phone size={13} /> {it.phone}
                      </a>
                    )}
                    <span className="text-white/35">
                      {new Date(it.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Select
                    value={it.status}
                    onValueChange={(v) => setStatus(it.id, v)}
                  >
                    <SelectTrigger
                      className="w-[140px] bg-[#0e0e0e] border-white/12 rounded-none h-10 text-white text-sm focus:ring-0"
                      data-testid={`enquiry-status-select-${it.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-white/15 text-white rounded-none">
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => onConvert && onConvert(it)}
                    className="flex items-center gap-2 h-10 px-3 border border-white/12 text-white/60 hover:text-white hover:border-[#e31e24] transition-colors font-mono-rp text-[10px] tracking-[0.15em] uppercase"
                    data-testid={`enquiry-convert-${it.id}`}
                    title="Create an order from this enquiry"
                  >
                    <ArrowRightLeft size={14} /> To Order
                  </button>
                  <button
                    onClick={() => remove(it.id)}
                    className="grid place-items-center h-10 w-10 border border-white/12 text-white/50 hover:text-[#e31e24] hover:border-[#e31e24] transition-colors"
                    data-testid={`enquiry-delete-${it.id}`}
                    aria-label="Delete enquiry"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {it.message && (
                <p className="mt-4 text-white/70 text-sm bg-[#0e0e0e] border border-white/8 p-4 whitespace-pre-wrap">
                  {it.message}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
